/**
 * Client-side image optimization API
 * Handles file uploads and communicates with backend optimization service
 */
class ImageOptimizationAPI {
  constructor() {
    // Use same domain by default, or override with env var
    this.baseUrl = import.meta.env.VITE_UPLOAD_API_URL || '/api';
  }

  /**
   * Upload and optimize image via API
   */
  async uploadAndOptimizeImage(file, vehicleName, vehicleType) {
    try {
      // Validate file
      const validation = this.validateFile(file);
      if (!validation.valid) {
        throw new Error(validation.errors.join(', '));
      }

      console.log(`📤 Uploading and optimizing: ${file.name}`);

      // Create FormData
      const formData = new FormData();
      formData.append('image', file);
      formData.append('vehicleName', vehicleName);
      formData.append('vehicleType', vehicleType);

      // Upload to API endpoint
      const response = await fetch(`${this.baseUrl}/optimize-image`, {
        method: 'POST',
        body: formData,
      });

      const contentType = response.headers.get('content-type') || '';
      if (!response.ok) {
        if (contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Upload failed');
        }
        throw new Error('Upload failed (non-JSON response). Ensure API is configured.');
      }

      if (!contentType.includes('application/json')) {
        throw new Error('Unexpected response from server (HTML). Ensure API endpoint is reachable.');
      }

      const result = await response.json();
      
      console.log(`✅ Upload and optimization complete!`);
      console.log(`   File: ${file.name}`);
      console.log(`   Size reduction: ${result.savings}%`);
      
      return {
        success: true,
        optimizedPath: result.optimizedPath,
        originalSize: file.size,
        optimizedSize: result.optimizedSize,
        savings: result.savings
      };

    } catch (error) {
      console.error('❌ Upload and optimization failed:', error.message);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Validate uploaded file
   */
  validateFile(file) {
    const errors = [];
    const maxFileSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];

    if (!file) {
      errors.push('No file selected');
      return { valid: false, errors };
    }

    if (file.size > maxFileSize) {
      errors.push(`File size must be less than ${maxFileSize / (1024 * 1024)}MB`);
    }

    if (!allowedTypes.includes(file.type)) {
      errors.push(`File type must be one of: ${allowedTypes.join(', ')}`);
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Convert file to base64 for preview
   */
  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Get file size in human readable format
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

// Create singleton instance
const imageOptimizationAPI = new ImageOptimizationAPI();

export default imageOptimizationAPI;
